import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { CheckCircle2, Loader2 } from 'lucide-react';

export const SampleListForm = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [icp, setIcp] = useState('');
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || icp.trim().length < 5) {
      toast({ title: 'Missing details', description: 'Please fill in your name, email, and a short ICP description.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-sample-list-request', {
        body: { firstName, lastName, email, company: company || undefined, icp, emailConsent: consent },
      });
      if (error || (data && (data as any).error)) {
        throw new Error((data as any)?.error || error?.message || 'Submission failed');
      }
      setDone(true);
      toast({ title: 'Request received', description: "We'll build your sample list and be in touch shortly." });
    } catch (err: any) {
      toast({ title: 'Something went wrong', description: err.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-primary mb-4" />
        <h3 className="text-2xl font-bold text-foreground mb-2">Request received</h3>
        <p className="text-muted-foreground">
          We'll build your sample list and send a CSV to <span className="font-semibold text-foreground">{email}</span>. Expect it by Friday.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-background p-6 md:p-8 space-y-4 text-left">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="sl-first">First name *</Label>
          <Input id="sl-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={50} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sl-last">Last name *</Label>
          <Input id="sl-last" value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={50} />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="sl-email">Work email *</Label>
          <Input id="sl-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sl-company">Company</Label>
          <Input id="sl-company" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={100} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="sl-icp">Your ICP *</Label>
        <Textarea
          id="sl-icp"
          value={icp}
          onChange={(e) => setIcp(e.target.value)}
          required
          rows={4}
          maxLength={5000}
          placeholder="Industry, company size, titles, geography - whatever filters matter to you."
        />
      </div>
      <div className="flex items-start gap-2">
        <Checkbox id="sl-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-1" />
        <Label htmlFor="sl-consent" className="text-sm text-muted-foreground font-normal leading-relaxed">
          I agree to be contacted by Data Aviator about my request.
        </Label>
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="bg-red-600 hover:bg-red-700 text-white font-semibold w-full md:w-auto px-8 py-6 h-auto text-base"
      >
        {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending...</>) : "Send me a sample list"}
      </Button>
    </form>
  );
};
